<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1000" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center py-2 px-0">
          <span class="primary--text">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
      </div>

      <v-card-text class="pa-4 editor-content">
        <section class="mb-4">
          <label class="label-input">Section</label>
          <v-row>
            <v-col cols="12" md="8"><v-text-field v-model="fields.title" label="Name" hide-details/></v-col>
            <v-col cols="12" md="4">
              <v-checkbox v-model="fields.enableAnchor" label="Enable Anchor" hide-details />
            </v-col>
          </v-row>
        </section>

        <SectionSettingsContainerFuild
          class="mb-4"
          :settings="fields.settings"
          :container-page="fields.containerPage"
          :use-background-mobile="true"
          :show-container-page="false"
        />

        <section class="mb-4">
          <label class="label-input">Header</label>
          <div class="group-item">
            <p class="field-label">Title</p>
            <ckeditor :editor="editor" v-model="fields.header.title" :config="editorConfig" />
            <p class="field-label mt-4">Description</p>
            <ckeditor :editor="editor" v-model="fields.header.description" :config="editorConfig" />
            <ColorPicker
              class="mt-4"
              :value="fields.header.dividerColor"
              name="manchestore-banner-divider-color"
              label="Line Color"
              default-color="#FE9790"
              @input="value => $set(fields.header, 'dividerColor', value)"
            />
          </div>
        </section>

        <section class="mb-4">
          <label class="label-input">Video</label>
          <div class="group-item">
            <v-text-field v-model="fields.video.src" label="Video URL" hide-details />
            <video v-if="fields.video.src" class="video-preview" :src="fields.video.src" controls muted playsinline />
          </div>
        </section>

        <section class="mb-4">
          <div class="d-flex justify-space-between align-center mb-2">
            <label class="label-input mb-0">Card content</label>
            <v-btn small text color="primary" @click="handleAddItem"> <v-icon small>mdi-plus</v-icon>Add item </v-btn>
          </div>
          <div class="group-item mb-3">
            <v-text-field v-model="fields.card.title" label="Card title" hide-details />
          </div>
          <v-expansion-panels accordion>
            <v-expansion-panel v-for="(item, index) in fields.card.items" :key="`manchestore-banner-item-${index}`">
              <v-expansion-panel-header>
                <span>{{ item.text || 'Item ' + (index + 1) }}</span>
                <template #actions>
                  <v-btn icon @click.stop="handleRemoveItem(index)">
                    <v-icon color="error">mdi-delete</v-icon>
                  </v-btn>
                </template>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <p class="field-label">Image</p>
                <UploadImage
                  :image="{ imageSrc: item.image }"
                  height="160"
                  type-upload="image"
                  @callback="obj => handleUploadItemImage(obj, index)"
                  @callbackDelete="() => handleRemoveItemImage(index)"
                />
                <v-text-field v-model="item.text" class="mt-4" label="Text" hide-details />
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </section>
      </v-card-text>

      <div class="footer-modal">
        <v-card-actions class="py-2 px-0">
          <v-spacer />
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { updateHtmlFromData } from '../common/html/update.js';
import ColorPicker from './common/color-picker.vue';
import SectionSettingsContainerFuild from './common/section-settings-container-fuild.vue';

const defaultItem = (text = 'Item', image = '') => ({ text, image });

export default {
  name: 'ManchestoreBanner',
  components: { UploadImage, ColorPicker, SectionSettingsContainerFuild },
  props: { sectionEditor: { type: Object, required: true } },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      fields: {
        title: 'Manchestore banner',
        enableAnchor: false,
        settings: {
          paddingTop: false,
          paddingBottom: false,
          backgroundColor: '#F4EFEC',
          textColor: '#292929',
          overlayColor: '#000000',
          overlayOpacity: 0,
          showOverlay: false,
          backgroundImage: '',
          useBackgroundMobile: true,
          backgroundImageMobile: '',
        },
        containerPage: { enableStyle: false, background: '#FFFFFF' },
        header: { title: '', description: '', dividerColor: '#FE9790' },
        video: { src: '' },
        card: { title: '', items: [] },
      },
    };
  },
  mounted() {
    this.applyExtract(this.sectionEditor.sectionExtract);
  },
  watch: {
    sectionEditor: {
      handler(value) {
        if (value?.sectionExtract) this.applyExtract(value.sectionExtract);
      },
      deep: true,
    },
  },
  methods: {
    applyExtract(extract) {
      if (!extract) return;
      this.fields = {
        ...this.fields,
        ...extract,
        settings: { ...this.fields.settings, ...(extract.settings || {}) },
        containerPage: { ...this.fields.containerPage, ...(extract.containerPage || {}) },
        header: { ...this.fields.header, ...(extract.header || {}) },
        video: { ...this.fields.video, ...(extract.video || {}) },
        card: {
          ...this.fields.card,
          ...(extract.card || {}),
          items: Array.isArray(extract.card?.items)
            ? extract.card.items.map(item => ({ ...defaultItem(), ...item }))
            : [],
        },
      };
    },
    handleAddItem() {
      this.fields.card.items.push(defaultItem());
    },
    handleRemoveItem(index) {
      this.fields.card.items.splice(index, 1);
    },
    handleUploadItemImage(obj, index) {
      if (this.fields.card.items[index]) this.$set(this.fields.card.items[index], 'image', obj?.imageSrc || '');
    },
    handleRemoveItemImage(index) {
      if (this.fields.card.items[index]) this.$set(this.fields.card.items[index], 'image', '');
    },
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const html = updateHtmlFromData.sectionManchestoreBanner(this.sectionEditor.section.html, this.fields);
      this.$emit('update:section', { ...this.sectionEditor.section, title: this.fields.title, html });
      this.$emit('close');
    },
  },
};
</script>

<style scoped lang="scss">
.video-preview {
  display: block;
  width: 100%;
  max-height: 320px;
  margin-top: 16px;
  background: #000;
}
::v-deep .ck-editor__editable_inline {
  min-height: 90px;
}
</style>
